/* eslint-disable max-lines */
import { Dispatch } from "redux";

import { loadAndTrack } from "redux-request-loading";
import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { Payload } from "@neworbit/redux-helpers";

import { LinkObject } from "@teamprevent/common/model";
import { EditTaskCompletionDataModel, TaskModel, TaskToDeleteModel } from "@teamprevent/common/task/taskActions/models";
import { toast } from "@teamprevent/common/toasts";

import { CancelTaskModel, AddFileNoteModel, CreateTaskModel, CreateImmunisationTaskDto } from "../../task/model";
import { ApplicationState } from "../../applicationState";
import { EppDecision, Covid19VaccinationStatus } from "../../immunisations/model";
import { AddUnknownEmployeeModel } from "../../calendar/clinics/models";
import { employeeRecordApi } from "../employeeRecord/employeeRecordApi";

import { OhCaseDocumentApi } from "./ohCaseDocumentApi";
import {
    AppState,
    OhCaseDetailModel,
    OhCaseCreateModel,
    OhCaseListModel,
    UpdateAssociatedManagersPayload,
    UpdateAssociatedHrUsersPayload,
    LoadOhCaseDocumentsPayload,
    DeleteOhCaseDocumentPayload,
    CreateOhCaseDocumentPayload,
    ImmunisationCaseCreateModel,
    ActionTaskSuccessPayload,
    SetCaseConfidentialitySuccessPayload,
    UpdatePurchaseOrderNumberPayload,
    OhCaseDocument,
    CreateTasksSuccessPayload,
    UpdateOhCaseDocumentPayload
} from "./model";
import { ohCaseApi } from "./ohCaseApi";
import * as actions from "./actiontypes";
import { ohCaseTaskApi } from "./ohCaseTaskApi";

import { push } from "redux-little-router";

export type OhCaseAction =
    ({ type: typeof actions.LOAD_OHCASES_SUCCESS } & Payload<OhCaseListModel[]>)
    | ({ type: typeof actions.LOAD_DETAILED_OHCASES_SUCCESS } & Payload<(OhCaseDetailModel & { documents: OhCaseDocument[] })[]>)
    | ({ type: typeof actions.CREATE_OHCASE_SUCCESS } & Payload<OhCaseDetailModel>)
    | ({ type: typeof actions.LOAD_OHCASE_DETAIL_SUCCESS } & Payload<OhCaseDetailModel>)
    | ({ type: typeof actions.UPDATE_ASSOCIATED_MANAGERS_SUCCESS } & Payload<UpdateAssociatedManagersPayload>)
    | ({ type: typeof actions.UPDATE_ASSOCIATED_HR_USERS_SUCCESS } & Payload<UpdateAssociatedHrUsersPayload>)
    | ({ type: typeof actions.CREATE_TASK_SUCCESS } & Payload<TaskModel>)
    | ({ type: typeof actions.CREATE_TASKS_SUCCESS } & Payload<CreateTasksSuccessPayload>)
    | ({ type: typeof actions.CANCEL_TASK_SUCCESS } & Payload<CancelTaskModel>)
    | ({ type: typeof actions.ACTION_TASK_SUCCESS } & Payload<ActionTaskSuccessPayload>)
    | ({ type: typeof actions.CREATE_OHCASE_DOCUMENT_SUCCESS } & Payload<CreateOhCaseDocumentPayload>)
    | ({ type: typeof actions.UPDATE_OHCASE_DOCUMENT_SUCCESS } & Payload<UpdateOhCaseDocumentPayload>)
    | ({ type: typeof actions.LOAD_OHCASE_DOCUMENTS_SUCCESS } & Payload<LoadOhCaseDocumentsPayload>)
    | ({ type: typeof actions.DELETE_OHCASE_DOCUMENT_SUCCESS } & Payload<DeleteOhCaseDocumentPayload>)
    | ({ type: typeof actions.CREATE_IMMUNISATION_CASE_SUCCESS } & Payload<OhCaseDetailModel>)
    | ({ type: typeof actions.CREATE_BBV_CASE_SUCCESS } & Payload<OhCaseDetailModel>)
    | ({ type: typeof actions.CREATE_HEALTH_SURVEILLANCE_CASE_SUCCESS } & Payload<OhCaseDetailModel>)
    | ({ type: typeof actions.EDIT_TASK_COMPLETION_DATA_SUCCESS } & Payload<ActionTaskSuccessPayload>)
    | ({ type: typeof actions.DELETE_TASK_SUCCESS } & Payload<ActionTaskSuccessPayload>)
    | ({ type: typeof actions.SET_OHCASE_CONFIDENTIALITY_SUCCESS } & Payload<SetCaseConfidentialitySuccessPayload>)
    | ({ type: typeof actions.UPDATE_PURCHASE_ORDER_NUMBER_SUCCESS } & Payload<UpdatePurchaseOrderNumberPayload>);

export const loadOhCasesSuccess = (payload: OhCaseListModel[]): OhCaseAction => ({
    payload,
    type: actions.LOAD_OHCASES_SUCCESS
});

export const loadDetailedOhCasesSuccess = (payload: (OhCaseDetailModel & { documents: OhCaseDocument[] })[]): OhCaseAction => ({
    payload,
    type: actions.LOAD_DETAILED_OHCASES_SUCCESS
});

export const createOhCaseSuccess = (payload: OhCaseDetailModel): OhCaseAction => ({
    payload,
    type: actions.CREATE_OHCASE_SUCCESS
});

export const createBbvCaseSuccess = (payload: OhCaseDetailModel): OhCaseAction => ({
    payload,
    type: actions.CREATE_BBV_CASE_SUCCESS
});

export const createImmunisationCaseSuccess = (ohCaseDetails: OhCaseDetailModel): OhCaseAction => {
    return {
        type: actions.CREATE_IMMUNISATION_CASE_SUCCESS,
        payload: ohCaseDetails
    };
};

export const updateAssociatedManagersSuccess = (payload: UpdateAssociatedManagersPayload): OhCaseAction => ({
    payload,
    type: actions.UPDATE_ASSOCIATED_MANAGERS_SUCCESS
});

export const updateAssociatedHrUsersSuccess = (payload: UpdateAssociatedHrUsersPayload): OhCaseAction => ({
    payload,
    type: actions.UPDATE_ASSOCIATED_HR_USERS_SUCCESS
});

export const updatePurchaseOrderNumberSuccess = (payload: UpdatePurchaseOrderNumberPayload): OhCaseAction => ({
    payload,
    type: actions.UPDATE_PURCHASE_ORDER_NUMBER_SUCCESS
});

export const createTaskSuccess = (payload: TaskModel): OhCaseAction => ({
    payload,
    type: actions.CREATE_TASK_SUCCESS
});

export const createTasksSuccess = (payload: CreateTasksSuccessPayload): OhCaseAction => ({
    payload,
    type: actions.CREATE_TASKS_SUCCESS
});

export const cancelTaskSuccess = (payload: CancelTaskModel): OhCaseAction => ({
    payload,
    type: actions.CANCEL_TASK_SUCCESS
});

export const actionTaskSuccess = (payload: ActionTaskSuccessPayload): OhCaseAction => ({
    payload,
    type: actions.ACTION_TASK_SUCCESS
});

export const deleteTaskSuccess = (payload: ActionTaskSuccessPayload): OhCaseAction => ({
    payload,
    type: actions.DELETE_TASK_SUCCESS
});

export const loadOhCaseDetailSuccess = (ohCase: OhCaseDetailModel): OhCaseAction => ({
    payload: ohCase,
    type: actions.LOAD_OHCASE_DETAIL_SUCCESS
});

export const createOhCaseDocumentSuccess = (payload: CreateOhCaseDocumentPayload): OhCaseAction => ({
    payload,
    type: actions.CREATE_OHCASE_DOCUMENT_SUCCESS
});

export const updateOhCaseDocumentSuccess = (payload: UpdateOhCaseDocumentPayload): OhCaseAction => ({
    payload,
    type: actions.UPDATE_OHCASE_DOCUMENT_SUCCESS
});

export const loadOhCaseDocumentsSuccess = (payload: LoadOhCaseDocumentsPayload): OhCaseAction => ({
    payload,
    type: actions.LOAD_OHCASE_DOCUMENTS_SUCCESS
});

export const deleteOhCaseDocumentSuccess = (payload: DeleteOhCaseDocumentPayload): OhCaseAction => ({
    payload,
    type: actions.DELETE_OHCASE_DOCUMENT_SUCCESS
});

export const editTaskCompletionDataSuccess = (payload: ActionTaskSuccessPayload): OhCaseAction => ({
    payload,
    type: actions.EDIT_TASK_COMPLETION_DATA_SUCCESS
});

export const setCaseConfidentialitySuccess = (payload: SetCaseConfidentialitySuccessPayload): OhCaseAction => ({
    payload,
    type: actions.SET_OHCASE_CONFIDENTIALITY_SUCCESS
});

export function createOhCase(ohCase: OhCaseCreateModel, path: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.CREATE_OHCASE, ohCaseApi.create(ohCase));
        dispatch(createOhCaseSuccess(result));
        dispatch(push(`${path}/${result.id}`, {}));
    };
}

export function createImmunisationCase(customerId: string, employeeId: string, immunisationCase: ImmunisationCaseCreateModel):
    ThunkAction<Promise<OhCaseDetailModel>, ApplicationState, undefined, any> {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        const result = await loadAndTrack(
            dispatch,
            actions.CREATE_IMMUNISATION_CASE,
            ohCaseApi.createImmunisationCase(customerId, employeeId, immunisationCase));
        dispatch(createImmunisationCaseSuccess(result.immunisationCase));
        if (result.linkedCase) {
            dispatch(loadOhCaseDetailSuccess(result.linkedCase));
        }
        return result.immunisationCase;
    };
}

export function createBbvCase(customerId: string, employeeRecordId: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.CREATE_BBV_CASE, ohCaseApi.createBbvCase(customerId, employeeRecordId));
        dispatch(createBbvCaseSuccess(result));
        return result;
    };
}

export function updateAssociatedManagers(customerId: string, ohCaseId: string, associatedManagers: LinkObject[]):
    ThunkAction<Promise<void>, AppState, undefined, any> {
    return async (dispatch: ThunkDispatch<AppState, undefined, any>) => {
        await loadAndTrack(dispatch, "updateAssociatedManagers", ohCaseApi.updateAssociatedManagers(customerId, ohCaseId, associatedManagers));
        dispatch(updateAssociatedManagersSuccess({ customerId, ohCaseId, associatedManagers }));
    };
}

export function updateAssociatedHrUsers(customerId: string, ohCaseId: string, associatedHrUsers: LinkObject[]):
    ThunkAction<Promise<void>, AppState, undefined, any> {
    return async (dispatch: ThunkDispatch<AppState, undefined, any>) => {
        await loadAndTrack(dispatch, "updateAssociatedManagers", ohCaseApi.updateAssociatedHrUsers(customerId, ohCaseId, associatedHrUsers));
        dispatch(updateAssociatedHrUsersSuccess({ customerId, ohCaseId, associatedHrUsers }));
    };
}

export function updatePurchaseOrderNumber(customerId: string, ohCaseId: string, purchaseOrderNumber: string):
    ThunkAction<Promise<void>, AppState, undefined, any> {
    return async (dispatch: ThunkDispatch<AppState, undefined, any>) => {
        await loadAndTrack(dispatch, "updatePurchaseOrderNumber", ohCaseApi.updatePurchaseOrderNumber(customerId, ohCaseId, purchaseOrderNumber));
        toast.success("Purchase order number saved successfully");
        dispatch(updatePurchaseOrderNumberSuccess({ customerId, ohCaseId, purchaseOrderNumber }));
    };
}

export function loadDetailedOhCases({ customerId, employeeRecordId }: { customerId: string; employeeRecordId: string }) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_OHCASE_DETAILS, ohCaseApi.getAllForEmployeeRecordWithDetails(customerId, employeeRecordId));
        dispatch(loadDetailedOhCasesSuccess(result));
    };
}

export function loadOhCases({ customerId, employeeRecordId }: { customerId: string; employeeRecordId: string }) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_OH_CASES, ohCaseApi.getAllForEmployeeRecord(customerId, employeeRecordId));
        dispatch(loadOhCasesSuccess(result));
    };
}

export function createTask(customerId: string, caseId: string, task: CreateTaskModel) {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, actions.CREATE_OHCASE_TASK, ohCaseTaskApi.createTask(customerId, caseId, task));  // eslint-disable-line deprecation/deprecation, max-len
        dispatch(createTaskSuccess(response));
    };
}

export function createTasks(customerId: string, caseId: string, tasks: CreateTaskModel[]) {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, actions.CREATE_OHCASE_TASKS, ohCaseTaskApi.createTasks(customerId, caseId, tasks));
        dispatch(createTasksSuccess({ ohCaseId: caseId, tasks: response }));
    };
}

export function addFileNote(customerId: string, ohCaseId: string, model: AddFileNoteModel) {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, actions.ADD_FILE_NOTE, ohCaseApi.addFileNote(customerId, ohCaseId, model));
        dispatch(createTaskSuccess(response));
    };
}

export function cancelTask(ohCaseId: string, taskId: string, closingNote: string) {
    return async (dispatch: Dispatch) => {
        const tasks = await loadAndTrack(dispatch, `${actions.CANCEL_OHCASE_TASK}_${taskId}`, ohCaseTaskApi.cancelTask(ohCaseId, taskId, closingNote));
        dispatch(cancelTaskSuccess({ ohCaseId, taskId, tasks }));
        return tasks;
    };
}
export const actionTask = (customerId: string, ohCaseId: string, oldTaskId: string, notes: string, followUpTask: CreateImmunisationTaskDto) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, actions.ACTION_TASK, ohCaseTaskApi.actionTask(customerId, ohCaseId, oldTaskId, notes, followUpTask));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: response }));
    };
};

export const markAsImmsClear = (
    customerId: string,
    ohCaseId: string,
    eppDecision: EppDecision,
    eppInternalNotes: string,
    eppNotes: string,
    covid19VaccinationStatus: Covid19VaccinationStatus,
    bookedDateOfFirstVaccination: moment.Moment,
    dateOfFirstVaccination: moment.Moment,
    dateOfSecondVaccination: moment.Moment) => {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(
            dispatch,
            actions.MARK_OH_CASE_IMMS_CLEAR,
            ohCaseApi.markAsImmsClear(
                customerId,
                ohCaseId,
                eppDecision,
                eppInternalNotes,
                eppNotes,
                covid19VaccinationStatus,
                bookedDateOfFirstVaccination,
                dateOfFirstVaccination,
                dateOfSecondVaccination));
    };
};

export const loadOhCaseDocuments = ({ customerId, ohCaseId }: { customerId: string; ohCaseId: string }) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_OHCASE_DOCUMENTS, OhCaseDocumentApi.getAll(customerId, ohCaseId));
        dispatch(loadOhCaseDocumentsSuccess({ ohCaseId, documents: result }));
    };
};

export const deleteOhCaseDocument = (customerId: string, ohCaseId: string, ohCaseDocumentId: string) => {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(dispatch, actions.DELETE_OHCASE_DOCUMENT, OhCaseDocumentApi.deleteDocument(customerId, ohCaseId, ohCaseDocumentId));
        dispatch(deleteOhCaseDocumentSuccess({ ohCaseId, ohCaseDocumentId }));
    };
};

export function loadOhCaseDetail({ ohCaseId, customerId, forceLoad }: { ohCaseId: string; customerId: string; forceLoad?: boolean }) {
    return async (dispatch: Dispatch, getState: () => AppState) => {
        const ohCase = getState().ohCases.filter(c => c.id === ohCaseId)[0];
        if (ohCase === undefined
            || ohCase.detailUpdated === undefined
            || ohCase.detailUpdated.isBefore(ohCase.listUpdated)
            || forceLoad) {

            const action = ohCase === undefined ? createOhCaseSuccess : loadOhCaseDetailSuccess;

            const result = await loadAndTrack(dispatch, actions.LOAD_OHCASE_DETAIL, ohCaseApi.detail(customerId, ohCaseId));
            dispatch(action(result));
        }
    };
}

export function pushUrl(url: string) {
    return (dispatch: Dispatch) => {
        dispatch(push(url));
    };
}

export async function getPossibleOhCaseSearchDetails(caseId: number) {
    const possibleOhCases = await ohCaseApi.getPossibleOhCaseSearchDetails(caseId);
    const options = possibleOhCases.map(c => ({
        id: `/customers/${c.customerId}/employeeRecords/${c.employeeId}/ohCases/${c.ohCaseId}`,
        value: `/customers/${c.customerId}/employeeRecords/${c.employeeId}/ohCases/${c.ohCaseId}`,
        text: `${c.employeeName}`
    }));
    return options;
}

export const editTaskCompletionData = (customerId: string, ohCaseId: string, model: EditTaskCompletionDataModel) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, actions.EDIT_TASK_COMPLETION_DATA, ohCaseTaskApi.editTaskCompletionData(customerId, ohCaseId, model));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: [response] }));
    };
};

export const deleteTask = (customerId: string, ohCaseId: string, model: TaskToDeleteModel) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, actions.DELETE_TASK, ohCaseTaskApi.deleteTask(customerId, ohCaseId, model));
        dispatch(deleteTaskSuccess({ ohCaseId, tasks: response }));
    };
};

export const setCaseConfidentiality = (customerId: string, ohCaseId: string, isTotallyConfidential: boolean, reason: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(
            dispatch,
            actions.SET_OHCASE_CONFIDENTIALITY,
            ohCaseApi.setCaseConfidentiality(customerId, ohCaseId, isTotallyConfidential, reason)
        );
        dispatch(setCaseConfidentialitySuccess({ ohCaseId, totallyConfidential: isTotallyConfidential, tasks: response }));
    };
};

export const searchForEmployeeForCustomer = (customerId: string, employeeName: string) => {
    return async (dispatch: Dispatch) => {
        const result =
            await loadAndTrack(
                dispatch,
                actions.SEARCH_FOR_EMPLOYEE,
                employeeRecordApi.searchEmployeeRecordsOrGetAllEmployees(customerId, employeeName));
        return result;
    };
};

export const matchOhCaseToExistingEmployee = (customerId: string, ohCaseId: string, employeeRecordId: string) => {
    return async (dispatch: Dispatch) => {
        const result =
            await loadAndTrack(
                dispatch,
                actions.UPDATE_OHCASE_DETAILS,
                ohCaseApi.matchOhCaseToExistingEmployee(customerId, ohCaseId, employeeRecordId));
        dispatch(loadOhCaseDetailSuccess(result));
        dispatch(push(getNewCaseUrl(customerId, result.employeeRecordId, ohCaseId)));
    };
};

export const matchOhCaseToNewEmployee = (customerId: string, ohCaseId: string, employeeModel: AddUnknownEmployeeModel) => {
    return async (dispatch: Dispatch) => {
        const result =
            await loadAndTrack(
                dispatch,
                actions.UPDATE_OHCASE_DETAILS,
                ohCaseApi.matchOhCaseToNewEmployee(customerId, ohCaseId, employeeModel));
        dispatch(loadOhCaseDetailSuccess(result));
        dispatch(push(getNewCaseUrl(customerId, result.employeeRecordId, ohCaseId)));
    };
};

export const getOhCaseDocumentCount = (customerId: string, ohCaseId: string): Promise<number> => {
    return OhCaseDocumentApi.getCount(customerId, ohCaseId);
};

const getNewCaseUrl = (customerId: string, employeeRecordId: string, ohCaseId: string) =>
    `/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${ohCaseId}`;
